import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { Timer, Loader2, Zap, ArrowRight } from "lucide-react";
import { getQuickStudy } from "@/lib/api";

export const QuickStudy = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    setItems(null);
    try {
      const res = await getQuickStudy();
      setItems(res.items || []);
    } catch (e) {
      if (e?.response?.status === 402) setError("pro");
      else if (e?.response?.status === 400) setError("Generate a lecture and take a quiz first, then your 10-minute plan will target your weak spots.");
      else setError("Could not build your quick session. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (o) => {
    if (!o) { onClose(); setItems(null); setError(""); return; }
  };

  // load when opened fresh
  if (open && !loading && items === null && error === "") {
    load();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl">
            <Timer className="h-5 w-5 text-accent" /> I have 10 minutes
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" />
            <p className="mt-4 text-muted-foreground">Finding the 3 most important things for you to review…</p>
          </div>
        )}

        {error === "pro" && (
          <div className="py-8 text-center">
            <p className="text-muted-foreground">Quick study is a Lectura Pro feature.</p>
          </div>
        )}
        {error && error !== "pro" && (
          <div className="py-8 text-center text-muted-foreground">{error}</div>
        )}

        {items && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Focus on these three — based on your weakest areas:</p>
            <AnimatePresence>
              {items.map((it, i) => (
                <motion.div
                  key={i}
                  data-testid={`quick-item-${i}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl border border-border p-4"
                >
                  <div className="flex items-center gap-2">
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-accent text-accent-foreground text-sm font-bold">{i + 1}</span>
                    <h3 className="font-semibold">{it.concept}</h3>
                  </div>
                  <p className="mt-2 text-sm text-foreground/85 flex gap-2">
                    <Zap className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {it.key_point}
                  </p>
                  {it.why && <p className="mt-1.5 text-xs text-muted-foreground italic pl-6">{it.why}</p>}
                  {it.source && <p className="mt-1 text-xs text-muted-foreground pl-6">from {it.source}</p>}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
